<template>
    <div class="doc-table-block">
        <div class="doc-table-title">
            <div class="doc-table-title-text">
                <span v-if="table.cditem" class="doc-table-code">
                    {{ table.cditem }}
                </span>

                <span>{{ table.nmcaption }}</span>
            </div>

            <button
                type="button"
                class="btn-small"
                :disabled="disabled"
                @click="emit('add-row', { table })"
            >
                + Registro
            </button>
        </div>

        <div v-if="colunas.length === 0" class="doc-table-empty">
            Nenhum campo configurado para esta tabela.
        </div>

        <div v-else class="doc-table-wrapper">
            <table class="doc-table-grid">
                <thead>
                    <tr>
                        <th class="doc-table-actions-col">#</th>

                        <th
                            v-for="coluna in colunas"
                            :key="coluna.key"
                        >
                            <span>{{ coluna.nmcaption }}</span>
                            <span v-if="coluna.ckobrigatorio === 'S'" class="required">*</span>
                        </th>
                    </tr>
                </thead>

                <tbody>
                    <tr
                        v-for="linha in linhas"
                        :key="linha.nrtable_linha"
                    >
                        <td class="doc-table-actions-cell">
                            <div class="doc-table-row-actions">
                                <span class="doc-table-row-number">
                                    {{ linha.nrtable_linha }}
                                </span>

                                <button
                                    v-if="linha.nrtable_linha > 1"
                                    type="button"
                                    class="btn-icon--trash doc-table-delete-row"
                                    title="Excluir linha"
                                    :disabled="disabled"
                                    @click="abrirModal(linha)"
                                />
                            </div>
                        </td>

                        <td
                            v-for="coluna in colunas"
                            :key="`${linha.nrtable_linha}-${coluna.key}`"
                        >
                            <template v-if="itemDaLinha(linha, coluna)">
                                <DocDynamicInput
                                    v-if="itemDaLinha(linha, coluna).tpresposta === 'FIELD'"
                                    :tpfield="itemDaLinha(linha, coluna).tpfield || 'TEXTO'"
                                    :maxlength="itemDaLinha(linha, coluna).textlength"
                                    :model-value="getModelValue(itemDaLinha(linha, coluna))"
                                    @update:model-value="value => onInputField(itemDaLinha(linha, coluna), value)"
                                />

                                <textarea
                                    v-else-if="itemDaLinha(linha, coluna).tpresposta === 'MEMO'"
                                    class="doc-table-memo"
                                    :maxlength="itemDaLinha(linha, coluna).textlength || null"
                                    :value="itemDaLinha(linha, coluna).resposta_texto || ''"
                                    @input="event => atualizarItem(itemDaLinha(linha, coluna), { resposta_texto: event.target.value || null })"
                                />

                                <select
                                    v-else-if="itemDaLinha(linha, coluna).tpresposta === 'SELECT'"
                                    :value="itemDaLinha(linha, coluna).resposta_opcoes || ''"
                                    @change="event => atualizarItem(itemDaLinha(linha, coluna), { resposta_opcoes: event.target.value || null })"
                                >
                                    <option value="">Selecione...</option>
                                    <option
                                        v-for="opcao in parseOpcoes(itemDaLinha(linha, coluna).opcoes)"
                                        :key="opcao.value"
                                        :value="opcao.value"
                                    >
                                        {{ opcao.label }}
                                    </option>
                                </select>

                                <div
                                    v-else-if="itemDaLinha(linha, coluna).tpresposta === 'CHECK'"
                                    class="doc-table-check-group"
                                >
                                    <label
                                        v-for="opcao in parseOpcoes(itemDaLinha(linha, coluna).opcoes)"
                                        :key="opcao.value"
                                        class="doc-table-check-option"
                                    >
                                        <input
                                            type="checkbox"
                                            :value="opcao.value"
                                            :checked="valoresSelecionados(itemDaLinha(linha, coluna)).includes(opcao.value)"
                                            @change="event => onCheckChange(itemDaLinha(linha, coluna), opcao.value, event.target.checked)"
                                        />
                                        <span>{{ opcao.label }}</span>
                                    </label>
                                </div>

                                <DocRadioGroupRow
                                    v-else-if="itemDaLinha(linha, coluna).tpresposta === 'RADIO_GROUP'"
                                    :item="itemDaLinha(linha, coluna)"
                                    @update:item="emit('update:item', $event)"
                                />

                                <DocItemUnsupported
                                    v-else
                                    :item="itemDaLinha(linha, coluna)"
                                />

                                <textarea
                                    v-if="itemDaLinha(linha, coluna).ckinformacoes === 'S'"
                                    class="doc-table-info"
                                    placeholder="Informações complementares"
                                    :value="itemDaLinha(linha, coluna).informacoes || ''"
                                    @input="event => atualizarItem(itemDaLinha(linha, coluna), { informacoes: event.target.value || null })"
                                />
                            </template>

                            <span v-else class="doc-table-missing-cell">
                                —
                            </span>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>

    <ConfirmModal ref="confirmModal" />
</template>

<script setup>
import { computed, ref } from 'vue';

import DocDynamicInput from './DocDynamicInput.vue';
import DocItemUnsupported from './DocItemUnsupported.vue';
import DocRadioGroupRow from './DocRadioGroupRow.vue';
import { parseListaValores, parseOpcoes } from './docUtils';

import ConfirmModal from '../modals/ConfirmModal.vue';

const confirmModal = ref(null);

const props = defineProps({
    table: {
        type: Object,
        required: true
    },
    itens: {
        type: Array,
        default: () => []
    },
    disabled: {
        type: Boolean,
        default: false
    }
});

const emit = defineEmits(['update:item', 'add-row', 'delete-row']);

const itensOrdenados = computed(() => {
    return [...props.itens]
        .filter(item => item.tpitem === 'ITEM')
        .sort((a, b) => {
            const linhaA = Number(a.nrtable_linha || 1);
            const linhaB = Number(b.nrtable_linha || 1);

            if (linhaA !== linhaB) return linhaA - linhaB;

            const ordemA = Number(a.ordem ?? 0);
            const ordemB = Number(b.ordem ?? 0);

            if (ordemA !== ordemB) return ordemA - ordemB;

            return Number(a.id ?? 0) - Number(b.id ?? 0);
        });
});

const colunas = computed(() => {
    const map = new Map();

    itensOrdenados.value
        .filter(item => Number(item.nrtable_linha || 1) === 1)
        .forEach(item => {
            const key = chaveColuna(item);

            if (!map.has(key)) {
                map.set(key, {
                    key,
                    cditem: item.cditem,
                    ordem: Number(item.ordem ?? 0),
                    nmcaption: item.nmcaption,
                    ckobrigatorio: item.ckobrigatorio
                });
            }
        });

    return Array.from(map.values())
        .sort((a, b) => {
            if (a.ordem !== b.ordem) return a.ordem - b.ordem;
            return String(a.cditem || '').localeCompare(String(b.cditem || ''));
        });
});

const linhas = computed(() => {
    const map = new Map();

    itensOrdenados.value.forEach(item => {
        const nrLinha = Number(item.nrtable_linha || 1);

        if (!map.has(nrLinha)) {
            map.set(nrLinha, {
                nrtable_linha: nrLinha,
                itens: []
            });
        }

        map.get(nrLinha).itens.push(item);
    });

    return Array.from(map.values())
        .sort((a, b) => a.nrtable_linha - b.nrtable_linha);
});


async function abrirModal(obj) {
    const confirmado = await confirmModal.value.show({
        message: 'Confirma exclusão desta linha?',
        confirmText: 'Sim',
        cancelText: 'Não'
    })

    if (confirmado) {
        excluirLinha(obj);   
    }
}


function chaveColuna(item) {
    return item.cditem || `ORDEM_${item.ordem}`;
}

function itemDaLinha(linha, coluna) {
    return linha.itens.find(item => chaveColuna(item) === coluna.key) || null;
}

function getModelValue(item) {
    switch (item.tpfield) {
        case 'DATA':
            return item.resposta_data || '';

        case 'NUMERO':
            return item.resposta_numero ?? '';

        default:
            return item.resposta_texto || '';
    }
}

function atualizarItem(item, dados) {
    emit('update:item', {
        ...item,
        ...dados
    });
}

function onInputField(item, value) {
    switch (item.tpfield) {
        case 'DATA':
            atualizarItem(item, { resposta_data: value || null });
            break;

        case 'NUMERO':
            atualizarItem(item, { resposta_numero: value === '' ? null : Number(value) });
            break;

        default:
            atualizarItem(item, { resposta_texto: value || null });
            break;
    }
}

function valoresSelecionados(item) {
    return parseListaValores(item.resposta_opcoes);
}

function onCheckChange(item, value, checked) {
    let valores = valoresSelecionados(item);

    if (checked && !valores.includes(value)) {
        valores.push(value);
    }

    if (!checked) {
        valores = valores.filter(v => v !== value);
    }

    atualizarItem(item, {
        resposta_opcoes: valores.length ? valores.join(';') : null
    });
}

function excluirLinha(linha) {
    if (linha.nrtable_linha <= 1) return;

    const itemRef = linha.itens?.[0];

    if (!itemRef) return;

    emit('delete-row', itemRef);
}
</script>

<style scoped>
.doc-table-block {
    margin-top: 12px;
    border: 1px solid #d8e2ec;
    border-radius: 8px;
    background: #fff;
    overflow: hidden;
}

.doc-table-title {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding: 9px 10px;
    border-left: 4px solid #4a827c;
    background: #f2f8f7;
    font-weight: 700;
    color: #1f2937;
    text-align: left;
}

.doc-table-title-text {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
}

.doc-table-code {
    flex: 0 0 auto;
}

.doc-table-add-row {
    width: auto;
    flex: 0 0 auto;
}

.doc-table-empty {
    padding: 10px;
    font-size: 13px;
    color: #64748b;
}

.doc-table-wrapper {
    width: 100%;
    overflow-x: auto;
    padding: 8px;
}

.doc-table-grid {
    width: 100%;
    min-width: 760px;
    border-collapse: collapse;
}

.doc-table-grid th,
.doc-table-grid td {
    border: 1px solid #d7dee8;
    padding: 7px;
    vertical-align: top;
}

.doc-table-grid th {
    background: #f8fafc;
    color: #334155;
    font-size: 12px;
    font-weight: 700;
    text-align: left;
}

.doc-table-grid td {
    background: #fff;
}

.doc-table-actions-col {
    width: 54px;
    text-align: center !important;
}

.doc-table-actions-cell {
    width: 54px;
    text-align: center;
    background: #f8fafc !important;
}

.doc-table-row-actions {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
}

.doc-table-row-number {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 22px;
    height: 22px;
    border-radius: 999px;
    background: #e2e8f0;
    color: #334155;
    font-size: 12px;
    font-weight: 700;
}

.doc-table-delete-row {
    width: 18px !important;
    height: 18px !important;
}

.doc-table-memo {
    min-height: 70px;
    resize: vertical;
}

.doc-table-info {
    margin-top: 6px;
    min-height: 55px;
    resize: vertical;
    font-size: 12px;
}

.doc-table-check-group {
    display: flex;
    flex-direction: column;
    gap: 5px;
}

.doc-table-check-option {
    display: flex;
    align-items: flex-start;
    gap: 5px;
    font-weight: normal;
    cursor: pointer;
}

.doc-table-check-option input {
    width: 14px !important;
    height: 14px !important;
    min-height: 14px !important;
    margin-top: 2px;
    padding: 0;
}

.doc-table-missing-cell {
    display: inline-block;
    color: #94a3b8;
    padding: 5px 0;
}

.required {
    color: #b00020;
    margin-left: 3px;
}

@media (max-width: 900px) {
    .doc-table-title {
        align-items: flex-start;
        flex-direction: column;
    }

    .doc-table-grid {
        min-width: 680px;
    }
}
</style>
